import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import { useQueryParams } from '../hooks/useQueryParams';
import {
  Paper, Typography, Grid, Chip, Box,
  MenuItem, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Snackbar, Alert
} from '@mui/material';
import { Dashboard, List, Close, Upload, Download } from '@mui/icons-material';
import OnboardingDashboard from './OnboardingDashboard';
import ManufacturerChipRenderer from './ManufacturerChipRenderer';
import EditManufacturerData from './EditManufacturerData';
import FilterControls from './FilterControls';
import PaginationControls from './PaginationControls';
import NoAccountsMessage from './NoAccountsMessage';
import LoadingSkeletons from './LoadingSkeletons';
import AccountCardList from './AccountCardList';
import ManufacturerCount from './ManufacturerCount';
import apiClient from '../utils/axiosConfig';

// Utility function to get API URL safely
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  const fallbackUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : window.location.origin;
  return fallbackUrl;
};

const DEFAULT_ONBOARDING_STEPS = [
  { key: 'credentialsProvided', label: 'Credentials Provided (Client)' },
  { key: 'hl7Form', label: 'HL7 Retrieval Form Submission' },
  { key: 'hl7Config', label: 'HL7 Configuration in Servers' },
  { key: 'hl7Arrival', label: 'HL7 Arrival in the Servers' },
  { key: 'evokePortal', label: 'Visible in Evoke Portal - Reports' },
  { key: 'rpaSetup', label: 'Setup Manufacturer Crawler RPA' },
  { key: 'twilioNext', label: 'Twilio Call/SMS - Next Send' },
  { key: 'twilioFirst', label: 'Twilio Call/SMS - First Reminder' },
  { key: 'twilioSecond', label: 'Twilio Call/SMS - Second Reminder' },
  { key: 'twilioThird', label: 'Twilio Call/SMS - Third Reminder' },
  { key: 'postGrid', label: 'PostGrid - No Show Letter' },
  { key: 'qaVerification', label: 'QA Verification - Reports Transmitted' }
];

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired
};







function AccountOnboarding() {
  const { id } = useParams();
  const { queryParams, setQueryParam } = useQueryParams();
  
  // Get user from token verification
  const [user, setUser] = useState(null);
  const canEdit = user && ['admin', 'account_onboarding'].includes(user.role);
  
  // Verify token and get user info
  useEffect(() => {
    const checkUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await apiClient.get('/api/verify-token');
          setUser(response.data.user);
        } catch (error) {
          console.warn('Token verification failed:', error);
        }
      }
    };
    checkUser();
  }, []);
  const [onboardingData, setOnboardingData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoadingPaginated, setIsLoadingPaginated] = useState(false);
  const [pageSize, setPageSize] = useState(() => {
    return parseInt(queryParams.pageSize) || 15;
  });
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [dateFilter, setDateFilter] = useState({ fromDate: '', toDate: '' });
  const [accountNameFilter, setAccountNameFilter] = useState(queryParams.search || '');
  const [tabValue, setTabValue] = useState(() => {
    const tabParam = queryParams.tab;
    return tabParam === 'manage' ? 1 : 0;
  });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [customLabels, setCustomLabels] = useState({});
  const [customStatuses, setCustomStatuses] = useState({});
  const [newStatus, setNewStatus] = useState({});

  const [editingAccount, setEditingAccount] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [statusFilter, setStatusFilter] = useState(queryParams.status || '');
  const [newAccount, setNewAccount] = useState({
    manufacturers: [],
    accountName: '',
    evokePortalName: '',
    goLiveDate: '',
    goLiveStatus: false,
    manufacturerData: {}
  });
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [availableManufacturers, setAvailableManufacturers] = useState([]);

  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const [importPreview, setImportPreview] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    // Fetch data from backend
    fetchManufacturers();
    fetchOnboardingData();
  }, []);

  // Handle direct account access via URL parameter
  useEffect(() => {
    if (id && onboardingData.length > 0) {
      const account = onboardingData.find(acc => acc.id === parseInt(id));
      if (account && canEdit) {
        editAccount(account);
      }
    }
  }, [id, onboardingData, canEdit]);

  useEffect(() => {
    // Fetch paginated data when on manage accounts tab
    if (tabValue === 1) {
      fetchPaginatedData(1, '', pageSize);
    }
  }, [tabValue, pageSize]);

  const createInitialLabels = (manufacturerNames) => {
    const initialLabels = {};
    manufacturerNames.forEach(mfg => {
      initialLabels[mfg] = { ...DEFAULT_ONBOARDING_STEPS.reduce((acc, step) => ({ ...acc, [step.key]: step.label }), {}) };
    });
    return initialLabels;
  };

  const processManufacturersData = async (manufacturers) => {
    const manufacturerNames = manufacturers.map(m => m.manufacturer_name);
    setAvailableManufacturers(manufacturerNames);
    setCustomLabels(createInitialLabels(manufacturerNames));
    await fetchStatusOptionsForManufacturers(manufacturerNames);
  };

  const fetchManufacturers = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${getApiUrl()}/api/manufacturers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const manufacturers = await response.json();
        
        if (Array.isArray(manufacturers) && manufacturers.length > 0) {
          await processManufacturersData(manufacturers);
        }
      }
    } catch (error) {
      console.error('Failed to fetch manufacturers:', error);
    }
  };

  const fetchStatusForManufacturer = async (manufacturer, apiUrl, token) => {
    const response = await fetch(`${apiUrl}/api/onboarding/status-options/${encodeURIComponent(manufacturer)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const statuses = response.ok ? await response.json() : [];
    return { manufacturer, statuses };
  };

  const fetchStatusOptionsForManufacturers = async (manufacturers) => {
    const apiUrl = getApiUrl();
    const token = localStorage.getItem('token');
    
    const promises = manufacturers.map(manufacturer => 
      fetchStatusForManufacturer(manufacturer, apiUrl, token)
    );
    
    const results = await Promise.all(promises);
    const statusData = results.reduce((acc, { manufacturer, statuses }) => {
      acc[manufacturer] = statuses;
      return acc;
    }, {});
    
    setCustomStatuses(statusData);
  };

  const fetchOnboardingData = async () => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/onboarding-accounts?all=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOnboardingData(data);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding data:', error);
    }
  };

  const fetchPaginatedData = async (page = 1, search = '', limit = pageSize) => {
    setIsLoadingPaginated(true);
    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (search) params.append('search', search);
      
      const response = await fetch(`${apiUrl}/api/onboarding-accounts?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setPaginatedData(result.data || []);
        setPagination(result.pagination);
        // Update pageSize if it was passed as parameter
        if (limit !== pageSize) {
          setPageSize(limit);
          setQueryParam('pageSize', limit.toString());
        }
      }
    } catch (error) {
      console.error('Failed to fetch paginated data:', error);
    } finally {
      setIsLoadingPaginated(false);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    const currentPage = pagination?.currentPage || 1;
    const currentPageSize = pageSize;
    
    // Calculate new page to maintain user position
    const currentFirstItem = (currentPage - 1) * currentPageSize + 1;
    const newPage = Math.max(1, Math.ceil(currentFirstItem / newPageSize));
    
    setPageSize(newPageSize);
    setQueryParam('pageSize', newPageSize.toString());
    
    // Fetch data with new page size and calculated page
    if (tabValue === 1) {
      fetchPaginatedData(newPage, accountNameFilter, newPageSize);
    }
  };

  const getStatusColor = (status) => {
    return 'default';
  };

  const processAccountRequest = async (account) => {
    const payload = {
      accountName: account.accountName,
      manufacturers: account.manufacturers,
      manufacturerData: account.manufacturerData
    };
    
    const isUpdate = account.isUpdate && account.existingId;
    const url = isUpdate ? `/api/onboarding-accounts/${account.existingId}` : '/api/onboarding-accounts';
    
    const response = isUpdate 
      ? await apiClient.put(url, payload)
      : await apiClient.post(url, payload);
    
    return { success: true, account: account.accountName };
  };

  const processAccountSafely = async (account) => {
    try {
      return await processAccountRequest(account);
    } catch (error) {
      return { success: false, account: account.accountName, error: error.message };
    }
  };

  const processImportInChunks = async (accounts, chunkSize = 5) => {
    let successCount = 0;
    let errorCount = 0;
    const errorDetails = [];
    
    for (let i = 0; i < accounts.length; i += chunkSize) {
      const chunk = accounts.slice(i, i + chunkSize);
      const results = await Promise.all(chunk.map(processAccountSafely));
      
      results.forEach(result => {
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          errorDetails.push(result);
        }
      });
      
      if (i + chunkSize < accounts.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    return { successCount, errorCount, errorDetails };
  };

  const getFilterDisplayText = (filter) => {
    switch (filter) {
      case 'completed': return 'Completed';
      case 'inProgress': return 'In Progress';
      case 'notStarted': return 'Not Started';
      case 'goLive': return 'Go-Live';
      case 'all': return 'All';
      default: return 'Filtered';
    }
  };

  const isStepCompleted = (status, stepKey) => {
    if (stepKey === 'evokePortalName') {
      return status && status.trim() !== '';
    }
    const trimmedStatus = (status || '').trim();
    // Decode HTML entities for proper comparison
    const decodedStatus = trimmedStatus.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    return decodedStatus === 'Done' || 
           decodedStatus === 'Not Required' || 
           decodedStatus === 'Not Available' ||
           decodedStatus.startsWith('Sent By') || 
           decodedStatus.startsWith('Submitted By');
  };

  const isDataFieldCompleted = (value, fieldKey) => {
    if (fieldKey === 'remarks') return false; // Don't count remarks for metrics
    return value && value.trim() !== '';
  };

  const calculateProgress = React.useCallback((account, manufacturer = null) => {
    const steps = DEFAULT_ONBOARDING_STEPS.filter(step => 
      step.key !== 'credentials' && step.key !== 'goLiveDate'
    );
    const dataFields = ['credentials']; // Manufacturer-level fields only
    
    if (manufacturer) {
      const mfgData = account.manufacturerData[manufacturer] || {};
      
      // Count completed steps (10 steps)
      const completedSteps = steps.filter(step => 
        isStepCompleted(mfgData[step.key] || '', step.key)
      ).length;
      
      // Count completed manufacturer data fields (1 field: credentials)
      const completedMfgFields = dataFields.filter(field => 
        isDataFieldCompleted(mfgData[field] || '', field)
      ).length;
      
      // Count account-level fields (2 fields: evokePortalName, goLiveDate)
      const completedAccountFields = (
        (isDataFieldCompleted(account.evokePortalName || '', 'evokePortalName') ? 1 : 0) +
        (isDataFieldCompleted(account.goLiveDate || '', 'goLiveDate') ? 1 : 0)
      );
      
      const totalItems = steps.length + dataFields.length + 2; // 10 steps + 1 mfg field + 2 account fields = 13
      const completedItems = completedSteps + completedMfgFields + completedAccountFields;
      
      return totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    }
    
    if (!account.manufacturers?.length) return 0;
    
    const totalProgress = account.manufacturers.reduce((sum, mfg) => {
      const mfgData = account.manufacturerData[mfg] || {};
      
      // Count completed steps
      const completedSteps = steps.filter(step => 
        isStepCompleted(mfgData[step.key] || '', step.key)
      ).length;
      
      // Count completed manufacturer data fields (excluding remarks)
      const completedMfgDataFields = dataFields.filter(field => 
        isDataFieldCompleted(mfgData[field] || '', field)
      ).length;
      
      // Count account-level fields (shared across manufacturers)
      const completedAccountFields = (
        (isDataFieldCompleted(account.evokePortalName || '', 'evokePortalName') ? 1 : 0) +
        (isDataFieldCompleted(account.goLiveDate || '', 'goLiveDate') ? 1 : 0)
      );
      
      const totalItems = steps.length + dataFields.length + 2; // 10 steps + 1 mfg field + 2 account fields
      const completedItems = completedSteps + completedMfgDataFields + completedAccountFields;
      
      return sum + (totalItems > 0 ? (completedItems / totalItems) * 100 : 0);
    }, 0);
    
    // For overall account progress, calculate average across all manufacturers
    return totalProgress / account.manufacturers.length;
  }, []);

  const updateLocalAccountData = (accountId, manufacturer, field, value) => {
    setOnboardingData(prev => {
      const newData = [...prev];
      const accountIndex = newData.findIndex(account => account.id === accountId);
      if (accountIndex !== -1) {
        newData[accountIndex] = {
          ...newData[accountIndex],
          manufacturerData: {
            ...newData[accountIndex].manufacturerData,
            [manufacturer]: {
              ...newData[accountIndex].manufacturerData[manufacturer],
              [field]: field === 'goLiveDate' ? (value || null) : value
            }
          }
        };
      }
      return newData;
    });
  };





  const updatePaginatedDataState = (accountId, manufacturer, field, value) => {
    setPaginatedData(prev => {
      const newData = [...prev];
      const accountIndex = newData.findIndex(account => account.id === accountId);
      if (accountIndex !== -1) {
        newData[accountIndex] = {
          ...newData[accountIndex],
          manufacturerData: {
            ...newData[accountIndex].manufacturerData,
            [manufacturer]: {
              ...newData[accountIndex].manufacturerData[manufacturer],
              [field]: field === 'goLiveDate' ? (value || null) : value
            }
          }
        };
      }
      return newData;
    });
  };



  const updateDataStates = React.useCallback((accountId, manufacturer, field, value) => {
    updateLocalAccountData(accountId, manufacturer, field, value);
    if (tabValue === 1) {
      updatePaginatedDataState(accountId, manufacturer, field, value);
    }
  }, [tabValue]);

  const updateGoLiveStatus = async (accountId, goLiveStatus) => {
    try {
      await apiClient.put(`/api/onboarding-accounts/${accountId}/go-live-status`, {
        goLiveStatus
      });
      
      // Update local state
      setOnboardingData(prev => {
        const newData = [...prev];
        const accountIndex = newData.findIndex(account => account.id === accountId);
        if (accountIndex !== -1) {
          newData[accountIndex] = {
            ...newData[accountIndex],
            goLiveStatus
          };
        }
        return newData;
      });
      
      if (tabValue === 1) {
        setPaginatedData(prev => {
          const newData = [...prev];
          const accountIndex = newData.findIndex(account => account.id === accountId);
          if (accountIndex !== -1) {
            newData[accountIndex] = {
              ...newData[accountIndex],
              goLiveStatus
            };
          }
          return newData;
        });
      }
      
      const statusText = goLiveStatus ? 'Go-Live' : 'Not Go-Live';
      setSaveMessage(`Account go-live status updated to ${statusText}`);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to update go-live status:', error);
      setSaveMessage('Failed to update go-live status. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };
  


  const makeStepFieldRequest = async (accountId, encodedManufacturer, field, value) => {
    const url = `/api/onboarding-accounts/${accountId}/manufacturers/${encodedManufacturer}/steps/${field}`;
    const payload = { status: value };
    
    console.log(`Making step field request to: ${url}`, payload);
    
    const response = await apiClient.put(url, payload);
    
    console.log(`Step field request successful for ${field}`);
    return response.data;
  };

  const updateAccountStatus = React.useCallback((accountId, manufacturer, field, value) => {
    // Update UI immediately for instant feedback
    updateDataStates(accountId, manufacturer, field, value);
    
    // For dropdown fields (steps), save immediately to database
    const fieldMap = {
      'credentials': 'credentials',
      'remarks': 'remarks'
    };
    
    const isDataField = fieldMap[field];
    if (!isDataField) {
      // This is a step field (dropdown), save immediately
      const encodedManufacturer = encodeURIComponent(manufacturer);
      
      makeStepFieldRequest(accountId, encodedManufacturer, field, value)
        .then(() => {
          console.log(`Successfully updated ${field} to ${value} for ${manufacturer}`);
        })
        .catch(error => {
          console.error('Failed to update step status:', error);
          setSaveMessage(`Error updating ${field}: ${error.message}`);
          setTimeout(() => setSaveMessage(''), 3000);
        });
    }
  }, [updateDataStates]);



  const getStepsForManufacturer = React.useCallback((manufacturer) => {
    return DEFAULT_ONBOARDING_STEPS.map(step => ({
      ...step,
      label: customLabels[manufacturer]?.[step.key] || step.label
    }));
  }, [customLabels]);

  const getStatusesForManufacturer = (manufacturer) => {
    return customStatuses[manufacturer] || [];
  };

  const addCustomStatus = async (manufacturer, status) => {
    if (status && !customStatuses[manufacturer]?.includes(status)) {
      try {
        await apiClient.post('/api/onboarding/status-options', { manufacturer, status });
        await fetchStatusOptionsForSingleManufacturer(manufacturer);
      } catch (error) {
        console.error('Failed to add custom status:', error);
      }
    }
  };

  const fetchStatusOptionsForSingleManufacturer = async (manufacturer) => {
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/onboarding/status-options/${encodeURIComponent(manufacturer)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const statuses = await response.json();
        setCustomStatuses(prev => ({
          ...prev,
          [manufacturer]: statuses
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch status options for ${manufacturer}:`, error);
    }
  };

  const removeCustomStatus = async (manufacturer, status) => {
    // All statuses can be removed since they're all user-created
    
    try {
      await apiClient.delete(`/api/onboarding/status-options/${encodeURIComponent(manufacturer)}/${encodeURIComponent(status)}`);
      await fetchStatusOptionsForSingleManufacturer(manufacturer);
    } catch (error) {
      console.error('Failed to remove custom status:', error);
    }
  };



  const createManufacturerDataForNewAccount = () => {
    const manufacturerData = {};
    newAccount.manufacturers.forEach(mfg => {
      manufacturerData[mfg] = {
        credentials: '',
        remarks: newAccount.remarks || ''
      };
    });
    return manufacturerData;
  };

  const handleAccountCreationSuccess = async () => {
    const accountName = newAccount.accountName;
    setSaveMessage(`Account "${accountName}" created successfully`);
    
    await fetchOnboardingData();
    if (tabValue === 1) {
      await fetchPaginatedData(1, '', pageSize);
    }
    
    setNewAccount({
      manufacturers: [],
      accountName: '',
      evokePortalName: '',
      goLiveDate: '',
      goLiveStatus: false,
      manufacturerData: {}
    });
    setAddDialogOpen(false);
  };

  const addNewAccount = async () => {
    if (newAccount.manufacturers.length && newAccount.accountName) {
      setIsCreatingAccount(true);
      try {
        const manufacturerData = createManufacturerDataForNewAccount();
        
        const payload = {
          accountName: newAccount.accountName,
          evokePortalName: newAccount.evokePortalName,
          goLiveDate: newAccount.goLiveDate,
          goLiveStatus: newAccount.goLiveStatus,
          manufacturers: newAccount.manufacturers,
          manufacturerData
        };
        
        await apiClient.post('/api/onboarding-accounts', payload);
        await handleAccountCreationSuccess();
      } catch (error) {
        console.error('Failed to add account:', error);
        setSaveMessage('Failed to create account. Please try again.');
      } finally {
        setIsCreatingAccount(false);
      }
    }
  };

  const deleteAccount = async (accountId) => {
    try {
      await apiClient.delete(`/api/onboarding-accounts/${accountId}`);
      // Refresh both datasets
      await fetchOnboardingData();
      if (tabValue === 1) {
        await fetchPaginatedData(1, '', pageSize);
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const editAccount = (account) => {
    setEditingAccount({ ...account });
    setEditDialogOpen(true);
  };

  const updateEditingAccount = async () => {
    if (editingAccount) {
      try {
        await apiClient.put(`/api/onboarding-accounts/${editingAccount.id}`, {
          accountName: editingAccount.accountName,
          evokePortalName: editingAccount.evokePortalName,
          goLiveDate: editingAccount.goLiveDate,
          goLiveStatus: editingAccount.goLiveStatus,
          manufacturers: editingAccount.manufacturers,
          manufacturerData: editingAccount.manufacturerData
        });
        
        // Update local state instead of refetching to maintain position
        setOnboardingData(prev => {
          const newData = [...prev];
          const accountIndex = newData.findIndex(account => account.id === editingAccount.id);
          if (accountIndex !== -1) {
            newData[accountIndex] = {
              ...editingAccount,
              // Preserve any server-side fields that might not be in editingAccount
              createdDate: newData[accountIndex].createdDate
            };
          }
          return newData;
        });
        
        if (tabValue === 1) {
          setPaginatedData(prev => {
            const newData = [...prev];
            const accountIndex = newData.findIndex(account => account.id === editingAccount.id);
            if (accountIndex !== -1) {
              newData[accountIndex] = {
                ...editingAccount,
                createdDate: newData[accountIndex].createdDate
              };
            }
            return newData;
          });
        }
        
        const accountName = editingAccount.accountName;
        setSaveMessage(`Account "${accountName}" updated successfully`);
        setEditDialogOpen(false);
        setEditingAccount(null);
      } catch (error) {
        console.error('Failed to update account:', error);
        setSaveMessage('Failed to update account. Please try again.');
      }
    }
  };

  const toggleAccountSelection = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) 
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedAccounts(prev => 
      prev.length === filteredData.length ? [] : filteredData.map(account => account.id)
    );
  };

  const createManufacturerData = (values) => {
    const stepMapping = {
      4: 'credentialsProvided', 5: 'hl7Form', 6: 'hl7Config', 7: 'hl7Arrival',
      8: 'evokePortal', 9: 'rpaSetup', 10: 'twilioNext', 11: 'twilioFirst',
      12: 'twilioSecond', 13: 'twilioThird', 14: 'postGrid', 15: 'qaVerification'
    };
    
    const manufacturerData = {
      credentials: values[2] || '',
      remarks: values[16] || ''
    };
    
    Object.entries(stepMapping).forEach(([colIndex, stepKey]) => {
      const value = values[parseInt(colIndex)] || '';
      if (value && value !== 'NA' && value !== 'Not Available') {
        manufacturerData[stepKey] = value;
      }
    });
    
    return manufacturerData;
  };

  const updateExistingAccount = (existingAccount, currentManufacturer, manufacturerData) => {
    if (!existingAccount.manufacturers.includes(currentManufacturer)) {
      existingAccount.manufacturers.push(currentManufacturer);
      existingAccount.manufacturerData[currentManufacturer] = manufacturerData;
    }
  };

  const createNewAccount = (accountName, currentManufacturer, manufacturerData, goLiveDate) => {
    const existingSystemAccount = onboardingData.find(acc => 
      acc.accountName.toLowerCase() === accountName.toLowerCase()
    );
    
    return {
      accountName,
      evokePortalName: '',
      goLiveDate: goLiveDate || '',
      manufacturers: [currentManufacturer],
      manufacturerData: { [currentManufacturer]: manufacturerData },
      isUpdate: !!existingSystemAccount,
      existingId: existingSystemAccount?.id
    };
  };

  const addOrUpdateAccount = (accountsMap, accountName, currentManufacturer, manufacturerData, goLiveDate) => {
    if (accountsMap.has(accountName)) {
      const existingAccount = accountsMap.get(accountName);
      updateExistingAccount(existingAccount, currentManufacturer, manufacturerData);
      // Update go live date if provided
      if (goLiveDate && !existingAccount.goLiveDate) {
        existingAccount.goLiveDate = goLiveDate;
      }
    } else {
      const newAccount = createNewAccount(accountName, currentManufacturer, manufacturerData, goLiveDate);
      accountsMap.set(accountName, newAccount);
    }
  };

  const processCSVLine = (values, currentManufacturer, accountsMap) => {
    if (!values[1] || values[1] === '' || !currentManufacturer) {
      return currentManufacturer;
    }
    
    if (!availableManufacturers.includes(currentManufacturer)) {
      console.warn(`Manufacturer '${currentManufacturer}' not found in system`);
      return currentManufacturer;
    }
    
    const manufacturerData = createManufacturerData(values);
    const goLiveDate = values[3] && values[3] !== 'NA' ? values[3] : '';
    addOrUpdateAccount(accountsMap, values[1], currentManufacturer, manufacturerData, goLiveDate);
    return currentManufacturer;
  };

  const parseCSVLine = (line) => {
    return line.split(',').map(v => v.trim().replace(/"/g, ''));
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const accountsMap = new Map();
    let currentManufacturer = '';
    
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      
      if (values[0]) {
        currentManufacturer = values[0];
      }
      
      currentManufacturer = processCSVLine(values, currentManufacturer, accountsMap);
    }
    
    return Array.from(accountsMap.values()).sort((a, b) => a.accountName.localeCompare(b.accountName));
  };

  const processFileContent = (csvText) => {
    const parsedAccounts = parseCSV(csvText);
    setImportPreview(parsedAccounts);
  };

  const showInvalidFileMessage = () => {
    setSaveMessage('Please select a valid CSV file.');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const readCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => processFileContent(e.target.result);
    reader.readAsText(file);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      readCsvFile(file);
    } else {
      showInvalidFileMessage();
    }
  };

  const importAccounts = async () => {
    if (importPreview.length === 0) return;
    
    setIsImporting(true);
    
    try {
      const { successCount, errorCount, errorDetails } = await processImportInChunks(importPreview);
      
      const newCount = importPreview.filter(acc => !acc.isUpdate).length;
      const updateCount = importPreview.filter(acc => acc.isUpdate).length;
      
      let message = `Import completed: ${successCount} successful (${newCount} new, ${updateCount} updated)`;
      
      if (errorCount > 0) {
        message += `, ${errorCount} errors:\n`;
        errorDetails.forEach(error => {
          message += `• ${error.account}: ${error.error}\n`;
        });
      }
      
      setSaveMessage(message);
      await fetchOnboardingData();
      if (tabValue === 1) {
        await fetchPaginatedData(1, '', pageSize);
      }
      setImportDialogOpen(false);
      setImportPreview([]);
    } finally {
      setIsImporting(false);
      setTimeout(() => setSaveMessage(''), 8000); // Longer timeout for detailed messages
    }
  };

  const getAccountNamesForDeletion = () => {
    return selectedAccounts.map(id => {
      const account = filteredData.find(acc => acc.id === id);
      return account?.accountName;
    }).filter(Boolean);
  };

  const deleteAccountById = async (accountId) => {
    try {
      await apiClient.delete(`/api/onboarding-accounts/${accountId}`);
      return true;
    } catch (error) {
      return false;
    }
  };

  const processBulkDeletion = async () => {
    let successCount = 0;
    let errorCount = 0;
    
    for (const accountId of selectedAccounts) {
      try {
        const success = await deleteAccountById(accountId);
        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }
    
    const errorSuffix = errorCount > 0 ? `, ${errorCount} errors` : '';
    setSaveMessage(`Bulk delete completed: ${successCount} accounts deleted${errorSuffix}`);
    setSelectedAccounts([]);
    await fetchOnboardingData();
    if (tabValue === 1) {
      await fetchPaginatedData(1, '', pageSize);
    }
  };

  const bulkDeleteAccounts = async () => {
    if (selectedAccounts.length === 0) return;
    
    const accountNames = getAccountNamesForDeletion();
    const accountText = selectedAccounts.length === 1 ? 'account' : 'accounts';
    const confirmMessage = `Are you sure you want to delete ${selectedAccounts.length} ${accountText}?\n\n${accountNames.join('\n')}`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await processBulkDeletion();
      } finally {
        setTimeout(() => setSaveMessage(''), 5000);
      }
    }
  };

  const createRowForManufacturer = (account, manufacturer) => {
    const mfgData = account.manufacturerData[manufacturer] || {};
    const steps = getStepsForManufacturer(manufacturer);
    
    const row = {
      'Account Name': account.accountName,
      'Manufacturer': manufacturer,
      'Created Date': account.createdDate,
      '1Password User Name': mfgData.credentials || '',
      'Go Live Date': account.goLiveDate || '',
      'Evoke Portal Name': account.evokePortalName || '',
      'Progress': `${Math.round(calculateProgress(account, manufacturer))}%`
    };
    
    steps.filter(step => step.key !== 'credentials' && step.key !== 'goLiveDate').forEach(step => {
      row[step.label] = mfgData[step.key] || '';
    });
    
    row['Remarks'] = mfgData.remarks || '';
    return row;
  };

  const generateCsvData = (accounts) => {
    const csvData = [];
    accounts.forEach(account => {
      account.manufacturers.forEach(manufacturer => {
        csvData.push(createRowForManufacturer(account, manufacturer));
      });
    });
    return csvData;
  };

  const formatCsvContent = (csvData) => {
    const headers = Object.keys(csvData[0]);
    return [
      headers.map(header => `"${header}"`).join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          return `"${value.toString().replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');
  };

  const downloadCsvFile = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getAccountsToExport = () => {
    return selectedAccounts.length > 0 
      ? filteredData.filter(account => selectedAccounts.includes(account.id))
      : filteredData;
  };

  const showExportMessage = (accountCount) => {
    const accountText = accountCount === 1 ? 'account' : 'accounts';
    setSaveMessage(`Exported ${accountCount} ${accountText} to CSV`);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const showNoDataMessage = () => {
    setSaveMessage('No data to export');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const exportToCSV = () => {
    const accountsToExport = getAccountsToExport();
    const csvData = generateCsvData(accountsToExport);

    if (csvData.length === 0) {
      showNoDataMessage();
      return;
    }

    const csvContent = formatCsvContent(csvData);
    const filename = `account-onboarding-${new Date().toISOString().split('T')[0]}.csv`;
    
    downloadCsvFile(csvContent, filename);
    showExportMessage(accountsToExport.length);
  };

  const matchesManufacturerFilter = (account) => {
    return !selectedManufacturer || account.manufacturers.includes(selectedManufacturer);
  };

  const matchesDateFilter = (account) => {
    const { fromDate, toDate } = dateFilter;
    if (!fromDate && !toDate) return true;
    
    const accountDate = account.createdDate;
    if (!accountDate) return false;
    
    // Convert server UTC date to user's local timezone for comparison
    const serverDate = new Date(accountDate);
    const localDateStr = serverDate.toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
    
    if (fromDate && localDateStr < fromDate) return false;
    if (toDate && localDateStr > toDate) return false;
    
    return true;
  };

  const matchesNameFilter = (account) => {
    return !accountNameFilter || account.accountName.toLowerCase().includes(accountNameFilter.toLowerCase());
  };

  const matchesStatusFilter = (account) => {
    if (!statusFilter || statusFilter === 'all') return true;
    
    const progress = Math.round(calculateProgress(account));
    switch (statusFilter) {
      case 'completed': return progress === 100;
      case 'inProgress': return progress > 0 && progress < 100;
      case 'notStarted': return progress === 0;
      case 'goLive': return account.goLiveStatus === true;
      default: return true;
    }
  };



  const getTotalCountForStatusFilter = React.useCallback(() => {
    if (!statusFilter || !Array.isArray(onboardingData)) return null;
    return onboardingData.filter(account => {
      const progress = Math.round(calculateProgress(account));
      switch (statusFilter) {
        case 'completed': return progress === 100;
        case 'inProgress': return progress > 0 && progress < 100;
        case 'notStarted': return progress === 0;
        case 'goLive': return account.goLiveStatus === true;
        case 'all': return true;
        default: return true;
      }
    }).length;
  }, [onboardingData, statusFilter, calculateProgress]);

  const getTotalFilteredCount = React.useCallback(() => {
    if (!Array.isArray(onboardingData)) return null;
    
    // Check if any filters are active
    const hasFilters = selectedManufacturer || dateFilter.fromDate || dateFilter.toDate || statusFilter;
    if (!hasFilters) return null;
    
    return onboardingData.filter(account => 
      matchesManufacturerFilter(account) &&
      matchesDateFilter(account) &&
      matchesStatusFilter(account)
    ).length;
  }, [onboardingData, selectedManufacturer, dateFilter.fromDate, dateFilter.toDate, statusFilter, calculateProgress]);

  const getClientSidePagination = React.useCallback(() => {
    const useClientSide = (statusFilter && statusFilter !== 'all') || selectedManufacturer || dateFilter.fromDate || dateFilter.toDate;
    
    if (!useClientSide || !pagination) return pagination;
    
    const totalFilteredCount = getTotalFilteredCount();
    if (totalFilteredCount === null) return pagination;
    
    const totalPages = Math.max(1, Math.ceil(totalFilteredCount / pageSize));
    const currentPage = Math.min(pagination.currentPage, totalPages);
    
    return {
      ...pagination,
      totalCount: totalFilteredCount,
      totalPages,
      currentPage,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      limit: pageSize
    };
  }, [pagination, statusFilter, selectedManufacturer, dateFilter.fromDate, dateFilter.toDate, getTotalFilteredCount, pageSize]);

  const filteredData = React.useMemo(() => {
    // Use full dataset when any filter is applied that requires client-side filtering
    const useFullDataset = (statusFilter && statusFilter !== 'all') || selectedManufacturer || dateFilter.fromDate || dateFilter.toDate;
    const dataToFilter = tabValue === 0 || useFullDataset ? (onboardingData || []) : (paginatedData || []);
    
    if (!Array.isArray(dataToFilter)) {
      return [];
    }
    
    const filtered = dataToFilter
      .filter(account => 
        matchesManufacturerFilter(account) &&
        matchesDateFilter(account) &&
        matchesNameFilter(account) &&
        matchesStatusFilter(account)
      )
      .sort((a, b) => a.accountName.localeCompare(b.accountName)); // Always sort alphabetically by account name
    
    // Apply client-side pagination when using full dataset with filters
    if (useFullDataset && pagination) {
      const startIndex = (pagination.currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return filtered.slice(startIndex, endIndex);
    }
    
    return filtered;
  }, [onboardingData, paginatedData, tabValue, selectedManufacturer, dateFilter.fromDate, dateFilter.toDate, accountNameFilter, statusFilter, pagination, pageSize]);

  return (
    <Box>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => {
              // Reset status filter when manually switching to manage accounts tab
              if (newValue === 1 && tabValue === 0) {
                setStatusFilter('');
              }
              setTabValue(newValue);
              if (newValue === 1) {
                setQueryParam('tab', 'manage');
              } else {
                setQueryParam('tab', '');
              }
            }}
          >
            <Tab icon={<Dashboard />} label="Dashboard" />
            <Tab icon={<List />} label="Manage Accounts" />
          </Tabs>
          {canEdit && tabValue === 1 && (
            <Box sx={{ display: 'flex', gap: 2, pr: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setImportDialogOpen(true)}
                size="small"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  }
                }}
              >
                Import CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportToCSV}
                disabled={onboardingData.length === 0}
                size="small"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  }
                }}
              >
                Export CSV{selectedAccounts.length > 0 ? ` (${selectedAccounts.length})` : ''}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <OnboardingDashboard 
          onboardingData={onboardingData} 
          onFilterAccounts={(filter) => {
            try {
              // Clear other filters when applying status filter
              setSelectedManufacturer('');
              setDateFilter({ fromDate: '', toDate: '' });
              setAccountNameFilter('');
              setSelectedAccounts([]);
              
              // Set the status filter
              setStatusFilter(filter);
              
              // Switch to Manage Accounts tab
              setTabValue(1);
              setQueryParam('tab', 'manage');
            } catch (error) {
              console.error('Error applying filter:', error);
            }
          }}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>

      {/* Filter and Add New Account */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FilterControls 
          statusFilter={statusFilter}
          selectedAccounts={selectedAccounts}
          filteredData={filteredData}
          toggleSelectAll={toggleSelectAll}
          selectedManufacturer={selectedManufacturer}
          setSelectedManufacturer={setSelectedManufacturer}
          availableManufacturers={availableManufacturers}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          accountNameFilter={accountNameFilter}
          setAccountNameFilter={(value) => {
            setAccountNameFilter(value);
            setQueryParam('search', value);
          }}
          tabValue={tabValue}
          fetchPaginatedData={fetchPaginatedData}
          setStatusFilter={(value) => {
            setStatusFilter(value);
            setQueryParam('status', value);
          }}
          canEdit={canEdit}
          setAddDialogOpen={setAddDialogOpen}
          setLabelDialogOpen={setLabelDialogOpen}
          bulkDeleteAccounts={bulkDeleteAccounts}
          pageSize={pageSize}
        />
      </Paper>

      {/* Add Account Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Add New Account
          <IconButton
            onClick={() => setAddDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                select
                fullWidth
                label="Manufacturers *"
                value={newAccount.manufacturers}
                onChange={(e) => setNewAccount(prev => ({ ...prev, manufacturers: e.target.value }))}
                size="small"
                slotProps={{
                  select: {
                    multiple: true,
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )
                  }
                }}
              >
                {availableManufacturers.map(mfg => (
                  <MenuItem key={mfg} value={mfg}>{mfg}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Account Name *"
                value={newAccount.accountName}
                onChange={(e) => setNewAccount(prev => ({ ...prev, accountName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Evoke Portal Name"
                value={newAccount.evokePortalName}
                onChange={(e) => setNewAccount(prev => ({ ...prev, evokePortalName: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Go Live Date"
                type="date"
                value={newAccount.goLiveDate || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setNewAccount(prev => ({ ...prev, goLiveDate: newValue || null }));
                }}
                onClick={(e) => e.target.showPicker?.()}
                size="small"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                select
                fullWidth
                label="Go-Live Status"
                value={newAccount.goLiveStatus ? 'true' : 'false'}
                onChange={(e) => setNewAccount(prev => ({ ...prev, goLiveStatus: e.target.value === 'true' }))}
                size="small"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              >
                <MenuItem value="false">Not Go-Live</MenuItem>
                <MenuItem value="true">Go-Live</MenuItem>
              </TextField>
            </Grid>
            {newAccount.manufacturers.length > 0 && (
              <Grid size={12}>
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'primary.50', 
                  border: '1px solid', 
                  borderColor: 'primary.200', 
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: 'primary.main', 
                    borderRadius: '50%' 
                  }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.dark",
                      fontWeight: 500
                    }}>
                    Onboarding workflow will be initialized for {newAccount.manufacturers.length} selected manufacturer{newAccount.manufacturers.length > 1 ? 's' : ''}. Individual step tracking can be configured after account creation.
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={2}
                value={newAccount.remarks}
                onChange={(e) => setNewAccount(prev => ({ ...prev, remarks: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={isCreatingAccount}>Cancel</Button>
          <Button 
            variant="outlined"
            onClick={addNewAccount}
            disabled={!newAccount.manufacturers.length || !newAccount.accountName || isCreatingAccount}
          >
            {isCreatingAccount ? 'Creating Account...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Status Options Dialog */}
      <Dialog open={labelDialogOpen} onClose={() => setLabelDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Manage Status Options
          <IconButton
            onClick={() => setLabelDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {availableManufacturers.length > 0 ? availableManufacturers.map(manufacturer => (
              <Grid key={manufacturer} size={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  {manufacturer}
                </Typography>
                <Grid container spacing={2}>

                </Grid>
                
                {/* Custom Status Management */}
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 2 }} color="primary">
                  Custom Status Options
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Add new status option"
                      value={newStatus[manufacturer] || ''}
                      onChange={(e) => setNewStatus(prev => ({ ...prev, [manufacturer]: e.target.value }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newStatus[manufacturer]) {
                          addCustomStatus(manufacturer, newStatus[manufacturer]);
                          setNewStatus(prev => ({ ...prev, [manufacturer]: '' }));
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (newStatus[manufacturer]) {
                          addCustomStatus(manufacturer, newStatus[manufacturer]);
                          setNewStatus(prev => ({ ...prev, [manufacturer]: '' }));
                        }
                      }}
                      disabled={!newStatus[manufacturer]}
                    >
                      Add Status
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getStatusesForManufacturer(manufacturer).map(status => (
                      <Chip
                        key={status}
                        label={status}
                        color={getStatusColor(status)}
                        onDelete={() => removeCustomStatus(manufacturer, status)}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )) : (
              <Grid size={12}>
                <Typography variant="body1" sx={{
                  color: "text.secondary"
                }}>
                  Loading manufacturers...
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="outlined"
            onClick={() => {
              setLabelDialogOpen(false);
              // Labels are already saved in real-time via updateCustomLabel
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Import CSV File
          <IconButton
            onClick={() => setImportDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              Upload a CSV file with account onboarding data. The file should have the same format as the exported CSV.
            </Typography>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ marginBottom: '16px' }}
            />
          </Box>
          
          {importPreview.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview ({importPreview.length} accounts found)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Manufacturers ({importPreview.reduce((sum, acc) => sum + acc.manufacturers.length, 0)} total)</TableCell>
                      <TableCell>Credentials</TableCell>
                      <TableCell>Go Live Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.slice(0, 15).map((account) => {
                      const rowBgColor = account.isUpdate ? 'action.hover' : 'inherit';
                      return (
                        <TableRow key={`import-preview-${account.accountName}`} sx={{ bgcolor: rowBgColor }}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{
                                fontWeight: 500
                              }}>
                                {account.accountName}
                              </Typography>
                              <ManufacturerCount count={account.manufacturers.length} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const chipLabel = account.isUpdate ? 'Update' : 'Create';
                              const chipColor = account.isUpdate ? 'warning' : 'success';
                              return (
                                <Chip 
                                  label={chipLabel} 
                                  size="small" 
                                  color={chipColor}
                                  variant="outlined"
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {account.manufacturers.map(mfg => (
                                <Chip key={mfg} label={mfg} size="small" sx={{ fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {account.manufacturers.map(mfg => 
                              account.manufacturerData[mfg]?.credentials
                            ).filter(Boolean).join(', ') || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {account.goLiveDate || 'N/A'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {importPreview.length > 15 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 1
                  }}>
                  Showing first 15 accounts. Total: {importPreview.length} accounts, {importPreview.filter(acc => acc.isUpdate).length} updates, {importPreview.filter(acc => !acc.isUpdate).length} new
                </Typography>
              )}

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={isImporting}>Cancel</Button>
          <Button 
            variant="outlined"
            onClick={importAccounts}
            disabled={importPreview.length === 0 || isImporting}
          >
            {(() => {
              const buttonText = isImporting ? 'Importing...' : `Import ${importPreview.length} Accounts`;
              return buttonText;
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit Account
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editingAccount && (
            <>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.dark",
                    fontWeight: 500,
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}>
                  *Note: Fields marked with 
                  <Chip 
                    label="Evoke" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600, fontStyle: 'normal' }}
                  />
                  are managed by Evoke stakeholders
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label="Account Name *"
                  value={editingAccount.accountName}
                  onChange={(e) => setEditingAccount(prev => ({ ...prev, accountName: e.target.value }))}
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Evoke Portal Name
                      <Chip 
                        label="Evoke" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    </Box>
                  }
                  value={editingAccount.evokePortalName || ''}
                  onChange={(e) => setEditingAccount(prev => ({ ...prev, evokePortalName: e.target.value }))}
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  fullWidth
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Go Live Date
                      <Chip 
                        label="Evoke" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    </Box>
                  }
                  type="date"
                  value={editingAccount.goLiveDate || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEditingAccount(prev => ({ ...prev, goLiveDate: newValue || null }));
                  }}
                  onClick={(e) => e.target.showPicker?.()}
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Go-Live Status
                      <Chip 
                        label="Evoke" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    </Box>
                  }
                  value={editingAccount.goLiveStatus ? 'true' : 'false'}
                  onChange={(e) => setEditingAccount(prev => ({ ...prev, goLiveStatus: e.target.value === 'true' }))}
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                >
                  <MenuItem value="false">Not Go-Live</MenuItem>
                  <MenuItem value="true">Go-Live</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <TextField
                  select
                  fullWidth
                  label="Manufacturers *"
                  value={editingAccount.manufacturers}
                  onChange={(e) => setEditingAccount(prev => ({ ...prev, manufacturers: e.target.value }))}
                  size="small"
                  slotProps={{
                    select: {
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) => <ManufacturerChipRenderer selected={selected} />
                    },

                    inputLabel: { shrink: true }
                  }}>
                  {availableManufacturers.map(mfg => (
                    <MenuItem key={mfg} value={mfg}>{mfg}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              {/* Manufacturer-specific data */}
              {editingAccount.manufacturers.map(manufacturer => (
                <EditManufacturerData
                  key={manufacturer}
                  manufacturer={manufacturer}
                  editingAccount={editingAccount}
                  setEditingAccount={setEditingAccount}
                  getStepsForManufacturer={getStepsForManufacturer}
                  getStatusesForManufacturer={getStatusesForManufacturer}
                  getStatusColor={getStatusColor}
                />
              ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={updateEditingAccount} 
            variant="outlined"
            disabled={(() => {
              return !editingAccount?.manufacturers?.length || !editingAccount?.accountName;
            })()}
          >
            Update Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Progress Cards */}
      <Grid container spacing={3}>
        {isLoadingPaginated ? (
          <LoadingSkeletons />
        ) : (
          <AccountCardList 
            filteredData={filteredData}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
            selectedAccounts={selectedAccounts}
            toggleAccountSelection={toggleAccountSelection}
            canEdit={canEdit}
            editAccount={editAccount}
            deleteAccount={deleteAccount}
            calculateProgress={calculateProgress}
            updateAccountStatus={updateAccountStatus}
            updateGoLiveStatus={updateGoLiveStatus}
            getApiUrl={getApiUrl}
            setSaveMessage={setSaveMessage}
            getStepsForManufacturer={getStepsForManufacturer}
            getStatusesForManufacturer={getStatusesForManufacturer}
            getStatusColor={getStatusColor}
          />
        )}
      </Grid>

        {/* Pagination Controls */}
        {tabValue === 1 && (pagination || isLoadingPaginated) && (
          <PaginationControls 
            pagination={getClientSidePagination()}
            isLoadingPaginated={isLoadingPaginated}
            fetchPaginatedData={fetchPaginatedData}
            accountNameFilter={accountNameFilter}
            filteredCount={filteredData.length}
            totalFilteredCount={getTotalFilteredCount()}
            hasActiveFilters={!!(statusFilter || selectedManufacturer || dateFilter || accountNameFilter)}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        )}

        {filteredData.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <NoAccountsMessage statusFilter={statusFilter} getFilterDisplayText={getFilterDisplayText} />
            {(selectedManufacturer || dateFilter.fromDate || dateFilter.toDate || accountNameFilter || statusFilter) && (
              <Button 
                variant="outlined" 
                onClick={() => {
                  setSelectedManufacturer('');
                  setDateFilter({ fromDate: '', toDate: '' });
                  setAccountNameFilter('');
                  setStatusFilter('');
                  if (tabValue === 1) fetchPaginatedData(1, '', pageSize);
                }}
                sx={{ mt: 2 }}
              >
                Clear All Filters
              </Button>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Save Notification */}
      <Snackbar
        open={!!saveMessage}
        autoHideDuration={3000}
        onClose={() => setSaveMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSaveMessage('')} severity="success" sx={{ width: '100%' }}>
          {saveMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AccountOnboarding;